import { useState, useEffect } from "react";
import { useLocation, useSearch } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, CheckCircle, XCircle, Loader2, Eye, EyeOff } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import freshOrderLogo from "../assets/freshorder-logo.png";

export default function SetPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState("");
  const [email, setEmail] = useState("");
  const [orgName, setOrgName] = useState("");
  const [success, setSuccess] = useState(false);
  const [orgSlug, setOrgSlug] = useState<string | null>(null);
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const { toast } = useToast();

  const token = new URLSearchParams(searchString).get("token");

  useEffect(() => {
    async function verifyToken() {
      if (!token) {
        setTokenError("No invitation token provided");
        setIsVerifying(false);
        return;
      }

      try {
        const response = await fetch("/api/auth/verify-token", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });

        const data = await response.json();

        if (response.ok && data.valid) {
          setTokenValid(true);
          setEmail(data.email || "");
          setOrgName(data.orgName || "FreshOrder");
        } else {
          setTokenError(data.message || "Invalid or expired invitation link");
        }
      } catch (error) {
        setTokenError("Unable to verify invitation. Please try again later.");
      }
      setIsVerifying(false);
    }

    verifyToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters",
        variant: "destructive",
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords are the same",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("/api/auth/set-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setOrgSlug(data.orgSlug);
        toast({
          title: "Password set successfully!",
          description: "You can now log in with your new password.",
        });
      } else {
        toast({
          title: "Failed to set password",
          description: data.message || "Please try again",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Unable to set password. Please try again.",
        variant: "destructive",
      });
    }

    setIsLoading(false);
  };

  const handleGoToLogin = () => {
    setLocation("/");
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <Card className="w-full max-w-md shadow-lg border border-gray-200">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-[#2B5397] mx-auto mb-4" />
            <p className="text-gray-600">Verifying your invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <Card className="w-full max-w-md shadow-lg border border-gray-200">
          <CardHeader className="text-center border-b border-gray-100 bg-gray-50/50 p-6">
            <img 
              src={freshOrderLogo} 
              alt="FreshOrder" 
              className="h-14 mx-auto mb-4"
            />
            <CardTitle className="text-xl font-semibold text-red-600 flex items-center justify-center gap-2">
              <XCircle className="h-5 w-5" />
              Invalid Invitation
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 text-center">
            <p className="text-gray-600 mb-6">{tokenError}</p>
            <p className="text-sm text-gray-500">
              If you believe this is an error, please contact your administrator to send a new invitation.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <Card className="w-full max-w-md shadow-lg border border-gray-200">
          <CardHeader className="text-center border-b border-gray-100 bg-gray-50/50 p-6">
            <img 
              src={freshOrderLogo} 
              alt="FreshOrder" 
              className="h-14 mx-auto mb-4"
            />
            <CardTitle className="text-xl font-semibold text-green-600 flex items-center justify-center gap-2">
              <CheckCircle className="h-5 w-5" />
              Password Set!
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 text-center">
            <p className="text-gray-600 mb-6">
              Your password has been set successfully. You can now log in to access your dashboard.
            </p>
            <Button 
              onClick={handleGoToLogin}
              className="w-full bg-[#2B5397] hover:bg-[#1e3a6a]"
              data-testid="button-go-to-login"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
      <Card className="w-full max-w-md shadow-lg border border-gray-200">
        <CardHeader className="text-center border-b border-gray-100 bg-gray-50/50 p-6">
          <img 
            src={freshOrderLogo} 
            alt="FreshOrder" 
            className="h-14 mx-auto mb-4"
          />
          <CardTitle className="text-xl font-semibold text-[#2B5397] flex items-center justify-center gap-2">
            <Lock className="h-5 w-5" />
            Set Your Password
          </CardTitle>
          <p className="text-sm text-gray-600 mt-2">
            Welcome to <span className="font-semibold">{orgName}</span>
          </p>
          {email && (
            <p className="text-sm text-gray-500 mt-1">
              Setting password for: <span className="font-medium">{email}</span>
            </p>
          )}
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="password" className="font-medium">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 bg-[rgb(243,244,246)]"
                  required
                  minLength={6}
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="button-toggle-password"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500">Must be at least 6 characters</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="font-medium">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 bg-[rgb(243,244,246)]"
                  required
                  data-testid="input-confirm-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#2B5397] hover:bg-[#1e3a6a]"
              disabled={isLoading || password.length < 6 || password !== confirmPassword}
              data-testid="button-set-password"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting Password...
                </>
              ) : (
                "Set Password"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
